import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { CesFrozenColumnDirective } from '../../../../directives/ces-frozen-column.directive';
import { roleDefinition } from '../../../../entity-definitions/role.definition';
import { ConfirmationHelperService } from '../../../../services/confirmation-helper.service';
import { EntityDialogService } from '../../../../services/entity-dialog.service';
import { ToastService } from '../../../../services/toast.service';
import { PermissionStore } from '../../../../stores/permission.store';
import { RolePermissionStore } from '../../../../stores/role-permission.store';
import { RoleStore } from '../../../../stores/role.store';
import { Permission } from '../../../../types/permission.interface';
import { PermissionScope } from '../../../../types/permission-scope.enum';
import { RolePermission } from '../../../../types/role-permission.interface';
import { Role, RolePermissionType } from '../../../../types/role.interface';
import { CesButtonComponent } from '../../../ui/ces-buttons';
import { CesButtonSecondaryComponent } from '../../../ui/ces-buttons/ces-button-secondary.component';
import { CesIconComponent } from '../../../ui/ces-icon/ces-icon.component';
import { CesTitleComponent } from '../../../ui/ces-route-title/ces-route-title.component';

import { CesTooltipAutoDismissDirective } from '../../../../directives/ces-tooltip-auto-dismiss.directive';
interface PermissionTypeOption {
  label: string;
  value: RolePermissionType;
}

interface RoleEditDeleteResult {
  __action: 'delete';
  entity: Role;
}

type RoleStoreApi = {
  entities(): Role[];
  loadAll(): Promise<Role[]>;
  create(entity: Role): Promise<Role | null>;
  update(id: number, changes: Partial<Role>): Promise<Role | null>;
  delete(id: number): Promise<void>;
};

type PermissionStoreApi = {
  entities(): Permission[];
  loadAll(): Promise<Permission[]>;
};

type RolePermissionStoreApi = {
  entities(): RolePermission[];
  loadAll(): Promise<RolePermission[]>;
  create(entity: RolePermission): Promise<RolePermission | null>;
  update(id: number, changes: Partial<RolePermission>): Promise<RolePermission | null>;
};

interface MatrixGroupRow {
  kind: 'group';
  labelKey: string;
}

interface MatrixPermissionRow {
  kind: 'permission';
  permission: Permission;
}

export type MatrixRow = MatrixGroupRow | MatrixPermissionRow;

interface PendingChange {
  roleId: number;
  permissionId: number;
  previous: RolePermissionType;
  next: RolePermissionType;
}

/** Permissions rendered as rows, grouped by the asset sidebar sections (same labels, same order). */
const PERMISSION_GROUPS: ReadonlyArray<{ labelKey: string; scopes: readonly PermissionScope[] }> = [
  {
    labelKey: 'navigation.sections.monitoring',
    scopes: [PermissionScope.Dashboard, PermissionScope.Campaigns, PermissionScope.AutomaticMeasures, PermissionScope.AnalysisPosts],
  },
  {
    labelKey: 'navigation.sections.analysis',
    scopes: [PermissionScope.Tables, PermissionScope.Graphics, PermissionScope.AutomaticPosts],
  },
  {
    labelKey: 'navigation.sections.assetConfiguration',
    scopes: [
      PermissionScope.Locations,
      PermissionScope.Measures,
      PermissionScope.Rights,
      PermissionScope.ManualImports,
      PermissionScope.Documents,
    ],
  },
];

/** Roles stay comfortably scannable up to this count; beyond it a name filter appears. */
const ROLE_FILTER_THRESHOLD = 6;

const RIGHTS_PERMISSION_ID: number = PermissionScope.Rights;

@Component({
  selector: 'app-roles-and-permissions',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CesTooltipAutoDismissDirective,
    CesButtonComponent,
    CesButtonSecondaryComponent,
    CesFrozenColumnDirective,
    CesIconComponent,
    CesTitleComponent,
    FormsModule,
    SelectModule,
    TableModule,
    TooltipModule,
    TranslocoModule,
  ],
  templateUrl: './roles-and-permissions.component.html',
  styleUrl: './roles-and-permissions.component.css',
})
export class RolesAndPermissionsComponent implements OnInit {
  protected readonly roleStore = inject(RoleStore) as unknown as RoleStoreApi;
  protected readonly permissionStore = inject(PermissionStore) as unknown as PermissionStoreApi;
  protected readonly rolePermissionStore = inject(RolePermissionStore) as unknown as RolePermissionStoreApi;
  private readonly dialogService = inject(EntityDialogService);
  private readonly confirmationHelper = inject(ConfirmationHelperService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslocoService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  roles: Role[] = [];
  permissions: Permission[] = [];
  rolePermissions: RolePermission[] = [];
  matrixRows: MatrixRow[] = [];
  roleDefinition = roleDefinition;

  roleFilter = '';
  dataLoadingCompleted = false;
  loadFailed = false;
  saving = false;

  options: PermissionTypeOption[] = [];

  readonly roleFilterThreshold = ROLE_FILTER_THRESHOLD;
  readonly skeletonRows = Array.from({ length: 8 }, (_, index) => index);

  private readonly pendingChanges = new Map<string, PendingChange>();

  private readonly permissionI18nKeys: Record<number, string> = {
    [PermissionScope.Dashboard]: 'navigation.routes.dashboard',
    [PermissionScope.Campaigns]: 'entities.campaign.plural',
    [PermissionScope.AutomaticMeasures]: 'navigation.routes.automaticMeasurements',
    [PermissionScope.AnalysisPosts]: 'navigation.routes.analysisPosts',
    [PermissionScope.Tables]: 'navigation.routes.tables',
    [PermissionScope.Graphics]: 'navigation.routes.graphs',
    [PermissionScope.AutomaticPosts]: 'navigation.routes.publications',
    [PermissionScope.Locations]: 'navigation.routes.locations',
    [PermissionScope.Measures]: 'entities.measure.plural',
    [PermissionScope.Rights]: 'navigation.routes.accessRights',
    [PermissionScope.ManualImports]: 'navigation.routes.manualImports',
    [PermissionScope.Documents]: 'common.labels.documents',
  } satisfies Record<PermissionScope, string>;

  ngOnInit(): void {
    this.translate
      .selectTranslation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.initOptions();
        this.cdr.markForCheck();
      });
    void this.loadData();
  }

  get visibleRoles(): Role[] {
    const needle = this.normalize(this.roleFilter);
    if (!needle) return this.roles;
    return this.roles.filter(role => this.normalize(role.name).includes(needle));
  }

  get pendingChangeCount(): number {
    return this.pendingChanges.size;
  }

  getPermissionName(permission: Permission): string {
    const key = this.permissionI18nKeys[permission.id];
    return key ? this.translate.translate(key) : permission.name;
  }

  /** Effective value: staged draft first, then the persisted row, then None. */
  getPermissionValue(roleId: number, permissionId: number): RolePermissionType {
    const pending = this.pendingChanges.get(this.cellKey(roleId, permissionId));
    if (pending) return pending.next;
    return this.getPersistedValue(roleId, permissionId);
  }

  getPermissionType(roleId: number, permissionId: number): PermissionTypeOption | undefined {
    const value = this.getPermissionValue(roleId, permissionId);
    return this.options.find(option => option.value === value) ?? this.options[0];
  }

  isDirty(roleId: number, permissionId: number): boolean {
    return this.pendingChanges.has(this.cellKey(roleId, permissionId));
  }

  cellAriaLabel(role: Role, permission: Permission): string {
    return `${this.getPermissionName(permission)} — ${role.name}`;
  }

  getColor(type: RolePermissionType): string {
    switch (type) {
      case RolePermissionType.None:
        return 'var(--p-gray-300)';
      case RolePermissionType.ReadOnly:
        return 'var(--p-blue-200)';
      case RolePermissionType.Write:
        return 'var(--p-green-100)';
    }
  }

  /** Stages the change locally; nothing is persisted until the user saves. */
  onPermissionChange(role: Role, permission: Permission, newOption: PermissionTypeOption | null | undefined): void {
    if (!newOption) return;
    const key = this.cellKey(role.id, permission.id);
    const persisted = this.getPersistedValue(role.id, permission.id);

    if (newOption.value === persisted) {
      this.pendingChanges.delete(key);
    } else {
      this.pendingChanges.set(key, {
        roleId: role.id,
        permissionId: permission.id,
        previous: persisted,
        next: newOption.value,
      });
    }
    this.cdr.markForCheck();
  }

  cancelChanges(): void {
    this.pendingChanges.clear();
    this.cdr.markForCheck();
  }

  async saveChanges(): Promise<void> {
    if (this.pendingChanges.size === 0 || this.saving) return;

    const changes = [...this.pendingChanges.values()];
    const rightsDowngrades = changes.filter(
      change =>
        change.permissionId === RIGHTS_PERMISSION_ID &&
        change.previous === RolePermissionType.Write &&
        change.next !== RolePermissionType.Write,
    );
    if (rightsDowngrades.length > 0) {
      const names = rightsDowngrades
        .map(change => this.roles.find(role => role.id === change.roleId)?.name ?? '')
        .filter(Boolean)
        .join(', ');
      const confirmed = await this.confirmationHelper.ask(
        'entities.role.messages.rightsDowngradeConfirm',
        'entities.role.messages.rightsDowngradeTitle',
        { name: names },
      );
      if (!confirmed) return;
    }

    this.saving = true;
    this.cdr.markForCheck();

    const results = await Promise.allSettled(changes.map(change => this.persistChange(change)));

    let failures = 0;
    results.forEach((result, index) => {
      const change = changes[index];
      if (!change) return;
      if (result.status === 'fulfilled') {
        this.pendingChanges.delete(this.cellKey(change.roleId, change.permissionId));
      } else {
        failures += 1;
        console.error(this.translate.translate('common.errors.permissionSaveFailed'), result.reason);
      }
    });

    this.saving = false;
    if (failures > 0) {
      this.toastService.error('entities.role.messages.saveError');
    } else {
      this.toastService.success('entities.role.messages.saveSuccess');
    }
    this.cdr.markForCheck();
  }

  addRole(): void {
    this.dialogService
      .openCreateDialog<Role>({
        header: this.roleDefinition.messages.dialogTitles.create(),
        entityDefinition: this.roleDefinition,
        mode: 'create',
      })
      .subscribe(formData => {
        if (!formData) return;
        if ('__action' in formData && formData.__action === 'delete') return;

        void this.roleStore
          .create(formData as Role)
          .then(() => this.loadData())
          .catch((error: unknown) => {
            this.toastService.error('common.errors.roleCreationFailed');
            console.error(this.translate.translate('common.errors.roleCreationFailed'), error);
          });
      });
  }

  onEditRole(role: Role): void {
    this.dialogService
      .openEditDialog<Role>(role, {
        header: this.roleDefinition.messages.dialogTitles.edit(),
        headerParams: { name: role.name },
        entityDefinition: this.roleDefinition,
        mode: 'edit',
        onDelete: entity => this.deleteRole(entity),
      })
      .subscribe(result => {
        if (!result) return;
        if ('__action' in result && result.__action === 'delete') {
          const deleteResult = result as RoleEditDeleteResult;
          this.deleteRole(deleteResult.entity);
          return;
        }
        void this.roleStore.update(role.id, result as Partial<Role>).then(() => {
          void this.loadData();
        });
      });
  }

  deleteRole(role: Role): void {
    if (role.isUsed) {
      this.toastService.warning('entities.role.messages.deleteError');
      return;
    }

    this.roleStore
      .delete(role.id)
      .then(() => {
        void this.loadData();
      })
      .catch((error: unknown) => {
        this.toastService.warning('entities.role.messages.deleteError');
        console.error(this.translate.translate('common.errors.roleDeletionFailed'), error);
      });
  }

  async loadData(): Promise<void> {
    this.dataLoadingCompleted = false;
    this.loadFailed = false;
    this.pendingChanges.clear();
    this.cdr.markForCheck();

    const results = await Promise.allSettled([
      this.roleStore.loadAll(),
      this.permissionStore.loadAll(),
      this.rolePermissionStore.loadAll(),
    ]);
    const failure = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
    if (failure) {
      this.loadFailed = true;
      console.error(this.translate.translate('common.errors.dataLoadFailed'), failure.reason);
      this.cdr.markForCheck();
      return;
    }

    this.roles = [...this.roleStore.entities()].sort((a, b) => a.name.localeCompare(b.name, this.translate.getActiveLang()));
    this.permissions = this.filterToCatalog(this.permissionStore.entities());
    this.rolePermissions = [...this.rolePermissionStore.entities()];
    this.matrixRows = this.buildMatrixRows();
    this.dataLoadingCompleted = true;
    this.cdr.markForCheck();
  }

  private getPersistedValue(roleId: number, permissionId: number): RolePermissionType {
    const found = this.rolePermissions.find(rp => rp.roleId === roleId && rp.permissionId === permissionId);
    return found?.type ?? RolePermissionType.None;
  }

  private async persistChange(change: PendingChange): Promise<void> {
    const existing = this.rolePermissions.find(rp => rp.roleId === change.roleId && rp.permissionId === change.permissionId);
    if (existing) {
      existing.type = change.next;
      try {
        await this.rolePermissionStore.update(existing.id, existing);
      } catch (error) {
        existing.type = change.previous;
        throw error;
      }
    } else if (change.next !== RolePermissionType.None) {
      const created = await this.rolePermissionStore.create({
        id: 0,
        roleId: change.roleId,
        permissionId: change.permissionId,
        name: '',
        type: change.next,
      });
      if (created) this.rolePermissions.push(created);
    }
  }

  private initOptions(): void {
    this.options = [
      { label: this.translate.translate('entities.role.permissions.none'), value: RolePermissionType.None },
      { label: this.translate.translate('entities.role.permissions.readOnly'), value: RolePermissionType.ReadOnly },
      { label: this.translate.translate('entities.role.permissions.write'), value: RolePermissionType.Write },
    ];
  }

  /** The PermissionScope enum is the catalog; anything else in the store is data pollution. */
  private filterToCatalog(permissions: Permission[]): Permission[] {
    const known = permissions.filter(p => p.id in this.permissionI18nKeys);
    const ignored = permissions.filter(p => !(p.id in this.permissionI18nKeys));
    if (ignored.length > 0) {
      console.warn(
        'Ignoring permissions outside the PermissionScope catalog:',
        ignored.map(p => p.id),
      );
    }
    return known.sort((a, b) => a.id - b.id);
  }

  private buildMatrixRows(): MatrixRow[] {
    const byId = new Map(this.permissions.map(permission => [permission.id, permission]));
    return PERMISSION_GROUPS.flatMap(group => {
      const rows: MatrixRow[] = group.scopes
        .filter(scope => byId.has(scope))
        .map(scope => ({ kind: 'permission' as const, permission: byId.get(scope) as Permission }));
      return rows.length > 0 ? [{ kind: 'group' as const, labelKey: group.labelKey }, ...rows] : [];
    });
  }

  private cellKey(roleId: number, permissionId: number): string {
    return `${roleId}:${permissionId}`;
  }

  private normalize(value: string): string {
    return value
      .toLocaleLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }
}
